import { v4 as uuidv4 } from "uuid";

import { sentryLog } from "@/utils/sentry-utils";

const STORAGE_KEY = "lime_ai_panel_width";
const THREAD_KEY = "lime_ai_thread_id";

export const AI_CHAT_MIN_WIDTH = 320;
export const AI_CHAT_MAX_WIDTH = 720;
export const AI_CHAT_DEFAULT_WIDTH = 420;
/** Content narrower than this stops being usable, so the panel yields first. */
const MIN_CONTENT_WIDTH = 480;

export function clampAiChatWidth(width: number, viewportWidth: number): number {
  if (!Number.isFinite(width)) {
    return AI_CHAT_DEFAULT_WIDTH;
  }

  const ceiling = Math.min(
    AI_CHAT_MAX_WIDTH,
    Math.max(AI_CHAT_MIN_WIDTH, viewportWidth - MIN_CONTENT_WIDTH),
  );

  return Math.min(Math.max(Math.round(width), AI_CHAT_MIN_WIDTH), ceiling);
}

/**
 * Read synchronously so the panel never renders at the default width and then
 * snaps — which is what a `useLocalStorage` hook hydrating in an effect does.
 */
export function readStoredAiChatWidth(viewportWidth: number): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === null) {
      return clampAiChatWidth(AI_CHAT_DEFAULT_WIDTH, viewportWidth);
    }
    return clampAiChatWidth(Number(stored), viewportWidth);
  } catch (error) {
    sentryLog.warn("Failed to read AI panel width", { error: String(error) });
    return clampAiChatWidth(AI_CHAT_DEFAULT_WIDTH, viewportWidth);
  }
}

export function storeAiChatWidth(width: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(width));
  } catch (error) {
    // A full or blocked store must not break the panel.
    sentryLog.warn("Failed to store AI panel width", { error: String(error) });
  }
}

/**
 * The conversation id, stable across reloads so the server keeps writing to
 * one thread and can hand the transcript back. A browser with no stored id
 * starts a new conversation rather than adopting the account's most recent
 * one — clearing site data is a reasonable way to ask for a fresh start.
 */
export function readOrCreateAiChatThreadId(): string {
  try {
    const stored = localStorage.getItem(THREAD_KEY);
    if (stored !== null && stored.length > 0 && stored.length <= 64) {
      return stored;
    }
    const created = uuidv4();
    localStorage.setItem(THREAD_KEY, created);
    return created;
  } catch (error) {
    // Private windows and blocked site data land here. The chat still works;
    // it just will not survive the reload.
    sentryLog.warn("Failed to read AI chat thread id", {
      error: String(error),
    });
    return uuidv4();
  }
}

/**
 * Mints a fresh conversation id and makes it the stored one, so a reload
 * restores the new thread rather than the abandoned one.
 *
 * Returns the id even when the write fails: the new conversation still works,
 * it just will not survive the reload — the same trade `readOrCreateAiChatThreadId`
 * makes.
 */
export function startNewAiChatThreadId(): string {
  const created = uuidv4();
  try {
    localStorage.setItem(THREAD_KEY, created);
  } catch (error) {
    sentryLog.warn("Failed to store new AI chat thread id", {
      error: String(error),
    });
  }
  return created;
}
